/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2022 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: temp-file-upload-service.ts
 */

// This service is an interface for all the temp-file-upload implementation services.

angular.module('prism-ui.components').service('TempFileUploadService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    $timeout: JS5Function,
    FileProgressService: JS5Object,
    TemporaryFileUploadResourceFactory: JS5Object,
) {

    //========================//
    // Abstract Functionality //
    //========================//

    let tempFileUploadServiceImplementations: JS5Object = {};

    this.registerTempFileUploadServiceImplementation = (fileDestinationType: string, service: JS5Object) => {
        if (_.has(tempFileUploadServiceImplementations, fileDestinationType)) {
            console.warn(`Replacing previous TempFileUploadService implementation for file destination type: ${fileDestinationType}`);
        }
        tempFileUploadServiceImplementations[fileDestinationType] = service;
    };

    this.getTempFileUploadServiceImplementation = (fileDestinationType: string) => {
        return tempFileUploadServiceImplementations[fileDestinationType];
    };

    this.getConfiguredTempFileUploadServiceImplementation = () => {
        return this.getTempDirectoryConfiguration().then((tempDirConfig: JS5Object) => {
            const implementation = tempFileUploadServiceImplementations[tempDirConfig.storageProvider];
            if (!implementation) {
                throw 'Webapp is not configured to upload to temp directory';
            }
            return implementation;
        });
    };


    //====================================//
    // Abstract File Upload Functionality //
    //====================================//

    const _initialize = (fileUploader: JS5Object) => {
        return this.getConfiguredTempFileUploadServiceImplementation().then((implementation: JS5Object) => {
            return implementation.initialize(fileUploader);
        });
    };

    this.initialize = _.memoize(_initialize);

    // Implementations should resolve uploadInfo.extraProps.deferredVirtualPath...
    // and return a JS5Promise
    this.uploadFileToTemp = (fileUploader: JS5Object, fileItem: JS5Object, index: number, uploadInfo: JS5Object, targetSubdirectory: string, targetPackageId: string) => {
        return this.initialize(fileUploader)
            .then(() => this.getConfiguredTempFileUploadServiceImplementation())
            .then((implementation: JS5Object) => {
                return implementation.uploadFileToTemp(fileUploader, fileItem, index, uploadInfo, targetSubdirectory, targetPackageId);
            });
    };

    this.cleanup = (fileUploader: JS5Object) => {
        this.initialize(fileUploader)
            .then(() => this.getConfiguredTempFileUploadServiceImplementation())
            .then((implementation: JS5Object) => {
                // Timeout ensures all upload promises are started before cleanup is called
                $timeout(() => {
                    this.initialize.cache.delete(fileUploader);
                    implementation.cleanup(fileUploader);
                });
            })
            .catch(() => {
                this.initialize.cache.delete(fileUploader);
            });
    };


    //========================//
    // Shared Implementations //
    //========================//

    this.tempDirConfigPromise = null;

    this.getTempDirectoryConfiguration = () => {
        if (!this.tempDirConfigPromise) {
            this.tempDirConfigPromise = TemporaryFileUploadResourceFactory.getConfiguration().$promise;
        }
        return this.tempDirConfigPromise;
    };

    this.getTempUploadCredentials = (fileName: string, destinationPath: string) => {
        return TemporaryFileUploadResourceFactory.getTemporaryUploadCredentials({
            name: fileName,
            destinationPath: destinationPath,
        }).$promise;
    };

    this.uploadFiles = (fileUploader: JS5Object, targetSubdirectory: string, targetPackageId: string) => {
        let uploadInfos: JS5Object = [];
        let fileItems = fileUploader.getNotUploadedItems();
        let findUploadInfoFn = (fileItem: JS5Object) => {
            return _.find(uploadInfos, (uploadInfo: JS5Object) => {
                return uploadInfo.extraProps.fileItem === fileItem;
            });
        };

        return fileItems.map((fileItem: JS5Object, index: number) => {
            let deferredVirtualPath = $q.defer();
            let uploadInfo = FileProgressService.addPendingUpload(fileItem._file.name, targetPackageId, {
                deferredVirtualPath: deferredVirtualPath,
                findUploadInfoForFileItem: findUploadInfoFn,
                cancelUploadFn: () => {
                    let cancelData = 'Upload was cancelled';
                    FileProgressService.cancelUpload(uploadInfo);
                    deferredVirtualPath.reject(cancelData);
                },
                fileItem: fileItem,
                allUploadInfos: uploadInfos,
            });
            uploadInfos.push(uploadInfo);
            let nestedUploadPromise = this.uploadFileToTemp(fileUploader, fileItem, index, uploadInfo, targetSubdirectory, targetPackageId)
                .catch((error: any) => {
                    FileProgressService.failUpload(uploadInfo, error);
                    deferredVirtualPath.reject(error);
                });

            let uploadPromise = deferredVirtualPath.promise
                .then(() => {
                    // Ensure virtualPath resolves before upload
                    let deferred = $q.defer();
                    $timeout(() => deferred.resolve());
                    return deferred.promise;
                })
                .then(() => nestedUploadPromise)
                .then((fileData: JS5Object) => {
                    FileProgressService.completeUpload(uploadInfo);
                    return fileData;
                });

            return {
                fileItem: fileItem,
                virtualPathPromise: deferredVirtualPath.promise,
                progressObservable: uploadInfo.progressObservable,
                uploadPromise: uploadPromise,
            };
        });
    };
});
